import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import styled from "styled-components";

import HostDetail from "../components/HostDetail";
import QRCodeDisplay from "../../../../components/QRCodeDisplay";
import DesktopPageHeader from "../../../../components/DesktopPageHeader";
import { useRestoUsers } from "../../../../hooks/useRestoUsers"
import { useUser } from "../../../../hooks/useUser";
import { useResto } from "../../../../hooks/useResto";
import { useOwnerRestaurantSelection } from "../../../../hooks/useOwnerRestaurantSelection";
import { useTransaction } from "../../../../hooks/useTransaction";
import { qrBaseUrl } from "../../../../Endpoint";

const getFirstActiveHost = (hosts = []) =>
    (Array.isArray(hosts) ? hosts : []).find((host) => host?.active !== false && (host?._id || host?.id)) || null;

export const HostTab = ({
    resto,
    canSeeHostEvaluations = false,
    canSeeHostQuizResults = false,
}) => {
    const { getAllRestoHost, loading, error } = useRestoUsers();
    const { isOwner, userId } = useUser()
    const { getResto, getPosition, restoPosition, getLikesCount, restoLikes } = useResto();
    const { points, pointsLoader } = useTransaction();
    const [selectedHost, setSelectedHost] = useState(null);
    const [directLoading, setDirectLoading] = useState(true);
    const [directError, setDirectError] = useState("");
    const [restoName, setRestoName] = useState("")
    const [bannerAvatar, setBannerAvatar] = useState("")
    const [restoAddress, setRestoAddress] = useState("")
    const [restoId, setRestoId] = useState("")
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const getAllRestoHostRef = useRef(getAllRestoHost);
    const {
        selectedOwnerRestaurantId,
        selectedOwnerRestaurant,
    } = useOwnerRestaurantSelection();

    useEffect(() => {
        getAllRestoHostRef.current = getAllRestoHost;
    }, [getAllRestoHost]);

    const loadDirectHost = useCallback(async (currentRestoId) => {
        try {
            setDirectLoading(true);
            setDirectError("");
            setSelectedHost(null);
            const hosts = await getAllRestoHostRef.current(currentRestoId);
            setSelectedHost(getFirstActiveHost(hosts));
        } catch (err) {
            console.error("Error cargando detalle de host:", err);
            setDirectError(err?.message || "Error al cargar el host");
            setSelectedHost(null);
        } finally {
            setDirectLoading(false);
        }
    }, [])

    useEffect(() => {
        let isMounted = true;
        const loadInitial = async () => {
            if (!isInitialLoad) return;
            try {
                setIsInitialLoad(false);
                if (!isOwner) {
                    let resolvedResto = resto;
                    if (!resolvedResto?._id && !resolvedResto?.id && !resolvedResto?.place_id) {
                        const restoResponse = await getResto();
                        resolvedResto = restoResponse?.data || restoResponse || null;
                    }
                    const currentRestoId =
                        resolvedResto?._id ||
                        resolvedResto?.id ||
                        resolvedResto?.place_id ||
                        resolvedResto?.restoId;
                    if (isMounted) {
                        await loadDirectHost(currentRestoId);
                    }
                }
            } catch (err) {
                console.error("Error cargando hosts iniciales:", err);
                if (isMounted) {
                    setIsInitialLoad(true);
                    setDirectError(err?.message || "Error al cargar el host");
                    setDirectLoading(false);
                }
            }
        };
        loadInitial();
        return () => {
            isMounted = false;
        };
    }, [getResto, isInitialLoad, isOwner, loadDirectHost, resto])

    useEffect(() => {
        if (!isOwner) return;
        const selected = selectedOwnerRestaurant;
        if (!selected) return;

        const selectedId = String(selectedOwnerRestaurantId || selected?._id || selected?.id || selected?.place_id || "");
        setRestoId(selected?._id || selected?.id || "");
        setRestoName(selected?.name || "");
        setRestoAddress(selected?.address || "");
        setBannerAvatar(selected?.brand || "");
        loadDirectHost(selectedId);
    }, [isOwner, loadDirectHost, selectedOwnerRestaurant, selectedOwnerRestaurantId])

    useEffect(() => {
        if (isOwner) return;
        if (resto) {
            setRestoId(resto?._id)
            setRestoName(resto?.name);
            setRestoAddress(resto?.address)
            setBannerAvatar(resto?.brand || "")
        }
    }, [resto, isOwner])

    useEffect(() => {
        getPosition();
        getLikesCount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOwnerRestaurantId, restoId])

    const selectedWarehouseId = useMemo(() => String(
        userId ||
        selectedOwnerRestaurantId ||
        selectedOwnerRestaurant?._id ||
        selectedOwnerRestaurant?.id ||
        selectedOwnerRestaurant?.place_id ||
        selectedOwnerRestaurant?.restoId ||
        restoId ||
        ""
    ), [restoId, selectedOwnerRestaurant, selectedOwnerRestaurantId, userId]);
    const evaluationUrl = isOwner && selectedWarehouseId
        ? `${qrBaseUrl}/w/${selectedWarehouseId}`
        : "";

    return (
        <ResponsiveContent>
            <DesktopPageHeader img={bannerAvatar} alt={restoName} />
            {evaluationUrl && (
                <QrPanel>
                    <QrTitle>QR de evaluación</QrTitle>
                    <QRCodeDisplay
                        text={evaluationUrl}
                        userName={restoName || "Almacén"}
                        displaySize={240}
                        sizeCm={3}
                        showUpdateButton={false}
                    />
                </QrPanel>
            )}
            {isOwner ? (
                <OwnerWarehouseContent>
                    <WarehouseSummary
                        name={restoName}
                        address={restoAddress}
                        likes={points?.likes}
                        points={points?.points}
                        labelLikes={points?.labelLikes}
                        labelPoints={points?.labelPoints}
                        loading={directLoading || pointsLoader}
                    />
                </OwnerWarehouseContent>
            ) : (
                <>
                    {(directLoading || (loading && !selectedHost)) && (
                        <HostDetailFrame>
                            <HostDetail
                                host={selectedHost}
                                loading
                                error={directError || error}
                                canSeeEvaluations={canSeeHostEvaluations}
                                canSeeQuizResults={canSeeHostQuizResults}
                                restoId={restoId}
                            />
                        </HostDetailFrame>
                    )}
                    {!directLoading && selectedHost && (
                        <HostDetailFrame>
                            <HostDetail
                                host={selectedHost}
                                loading={loading}
                                error={directError || error}
                                canSeeEvaluations={canSeeHostEvaluations}
                                canSeeQuizResults={canSeeHostQuizResults}
                                restoId={restoId}
                            />
                        </HostDetailFrame>
                    )}
                    {!directLoading && !selectedHost && (
                        <EmptyState>
                            <EmptyTitle>No hay host disponible</EmptyTitle>
                            <EmptyText>{directError || error || "Cuando exista un host activo, el detalle aparecerá aquí."}</EmptyText>
                        </EmptyState>
                    )}
                </>
            )}
            <MobileBottomSpacer />
        </ResponsiveContent>
    );
}

const ResponsiveContent = styled.div`
    width: 100%;
    max-width: 1200px;
    margin: 20px auto;
    min-width: 0;
    font-family: "Poppins", sans-serif;

    @media (max-width: 768px) {
        margin: 16px auto;
    }
`;

const QrPanel = styled.div`
    display: grid;
    gap: 10px;
    justify-items: center;
    padding: 14px 12px;
    margin-bottom: 18px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #ffffff;
`;

const QrTitle = styled.div`
    color: #111827;
    font-size: 13px;
    font-weight: 700;
`;

const MobileBottomSpacer = styled.div`
    display: none;

    @media (max-width: 768px) {
        display: block;
        height: 100px;
    }
`;

const HostDetailFrame = styled.div`
    width: 100%;
    min-width: 0;
    max-width: 100%;
`;

const WarehouseSummary = ({ name, address, likes, points, labelLikes, labelPoints, loading }) => {
    const safeLikes = Number.isFinite(Number(likes)) ? Number(likes) : 0;
    const safePoints = Number.isFinite(Number(points)) ? Number(points) : 0;

    return (
        <SummaryCard>
            <SummaryKicker>Tu almacén</SummaryKicker>
            <SummaryTitle>{name || "Almacén"}</SummaryTitle>
            {address && <SummaryText>{address}</SummaryText>}
            <SummaryGrid>
                <SummaryMetric>
                    <span>{labelLikes || "Puntos"}</span>
                    <strong>{loading ? "..." : safeLikes}</strong>
                </SummaryMetric>
                <SummaryMetric>
                    <span>{labelPoints || "Ñamis"}</span>
                    <strong>{loading ? "..." : safePoints}</strong>
                </SummaryMetric>
            </SummaryGrid>
        </SummaryCard>
    );
};

const OwnerWarehouseContent = styled.div`
    display: grid;
    gap: 16px;
    width: 100%;
    min-width: 0;
`;

const EmptyState = styled.div`
    display: grid;
    gap: 8px;
    padding: 32px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #ffffff;

    @media (max-width: 1024px) {
        padding: 24px;
    }
`;

const EmptyTitle = styled.h3`
    margin: 0;
    color: #111827;
    font-size: 18px;
`;

const EmptyText = styled.p`
    margin: 0;
    color: #6b7280;
    font-size: 14px;
`;

const SummaryCard = styled.div`
    display: grid;
    gap: 10px;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    padding: 24px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #ffffff;

    @media (max-width: 1024px) {
        padding: 20px;
    }
`;

const SummaryKicker = styled.div`
    color: #ff0102;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
`;

const SummaryTitle = styled.h3`
    margin: 0;
    color: #111827;
    font-size: 22px;
`;

const SummaryText = styled.p`
    margin: 0;
    color: #4b5563;
    font-size: 14px;
`;

const SummaryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin-top: 8px;
`;

const SummaryMetric = styled.div`
    display: grid;
    gap: 4px;
    padding: 14px;
    border: 1px solid #f3f4f6;
    border-radius: 8px;
    background: #fafafa;

    span {
        color: #6b7280;
        font-size: 12px;
    }

    strong {
        color: #111827;
        font-size: 22px;
    }
`;


